import { resolve, resolveSync } from "@ixfx/core";
import { zip } from "./util/zip.js";
/**
 * Returns a copy of `object`, with the same properties. For each property
 * that has a basic value (string, number, boolean, object), the value is set
 * for the return object. If the property is a function or generator, its value
 * is used instead. Async functions and generators are also usable.
 *
 * Use {@link resolveFieldsSync} for a synchronous version.
 *
 * Not recursive.
 *
 * In the below example, the function for the property `random` is invoked.
 * ```js
 * const state = {
 *  length: 10,
 *  random: () => Math.random();
 * }
 * const x = resolveFields(state);
 * // { length: 10, random: 0.1235 }
 * ```
 *
 * It also works with generators
 * ```js
 * import { count } from './numbers.js';
 *
 * const state = {
 *  length: 10,
 *  index: count(2) // Generator that yields: 0, 1 and then ends
 * }
 * resolveFields(state); // { length: 10, index: 0 }
 * resolveFields(state); // { length: 10, index: 1 }
 * // Generator finishes after counting twice:
 * resolveFields(state); // { length: 10, index: undefined }
 * ```
 * @param object
 * @returns
 */
export async function resolveFields(object) {
    const resolvers = [];
    const keys = [];
    for (const entry of Object.entries(object)) {
        const resolvable = entry[1];
        resolvers.push(resolve(resolvable));
        keys.push(entry[0]);
    }
    const results = await Promise.all(resolvers);
    const entries = zip(keys, results);
    return Object.fromEntries(entries);
}
export function resolveFieldsSync(object) {
    const entries = [];
    for (const entry of Object.entries(object)) {
        const resolvable = entry[1];
        const value = resolveSync(resolvable);
        entries.push([entry[0], value]);
    }
    return Object.fromEntries(entries);
}
/**
 * Returns a function that resolves `object`.
 *
 * Use {@link resolveFields} to resolve an object directly.
 * @param object
 * @returns
 */
// export function resolverFields<V extends object>(object: V) {
//   return () => resolveFields(object);
// }
